import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { LocalizationProvider } from './context/LocalizationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import DashboardHomePage from './pages/DashboardHomePage';
import TasksPage from './pages/TasksPage';
import TeamsPage from './pages/TeamsPage';
import ResourcesPage from './pages/ResourcesPage';
import RolePermissionsPage from './pages/RolePermissionsPage';
import ClockPage from './pages/ClockPage';
import UsersPage from './pages/UsersPage';
import ActivitiesPage from './pages/ActivitiesPage';
import JobTypesPage from './pages/JobTypesPage';
import ResourceTypesPage from './pages/ResourceTypesPage';
import AttendanceReportPage from './pages/AttendanceReportPage';
import TaskReportPage from './pages/TaskReportPage';
import UserReportPage from './pages/UserReportPage';
import ResourceReportPage from './pages/ResourceReportPage';
import CostReportPage from './pages/CostReportPage';
import AdminAvailabilityPage from './pages/AdminAvailabilityPage';
import LocalizationPage from './pages/LocalizationPage';
import './App.css';

function App() {
  const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

  return (
    <Router>
      <AuthProvider>
        <LocalizationProvider>
          <AppProvider>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <>
                    <Navbar />
                    <Navigate to="/dashboard" replace />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <>
                    <Navbar />
                    <DashboardHomePage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute requiredPermission={{ module: 'tasks', action: 'view' }}>
                  <>
                    <Navbar />
                    <TasksPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ProtectedRoute requiredPermission={{ module: 'teams', action: 'view' }}>
                  <>
                    <Navbar />
                    <TeamsPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources"
              element={
                <ProtectedRoute requiredPermission={{ module: 'resources', action: 'view' }}>
                  <>
                    <Navbar />
                    <ResourcesPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <Navigate to="/reports/attendance" replace />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/attendance"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <AttendanceReportPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/tasks"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <TaskReportPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/users"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <UserReportPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/resources"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <ResourceReportPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clock"
              element={
                <ProtectedRoute requiredPermission={{ module: 'availability', action: 'view' }}>
                  <>
                    <Navbar />
                    <ClockPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermission={{ module: 'users', action: 'view' }}>
                  <>
                    <Navbar />
                    <UsersPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute requiredPermission={{ module: 'permissions', action: 'view' }}>
                  <>
                    <Navbar />
                    <RolePermissionsPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities"
              element={
                <ProtectedRoute requiredPermission={{ module: 'logs', action: 'view' }}>
                  <>
                    <Navbar />
                    <ActivitiesPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-types"
              element={
                <ProtectedRoute requiredPermission={{ module: 'tasks', action: 'view' }}>
                  <>
                    <Navbar />
                    <JobTypesPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/resource-types"
              element={
                <ProtectedRoute requiredPermission={{ module: 'resource_types', action: 'view' }}>
                  <>
                    <Navbar />
                    <ResourceTypesPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/availability"
              element={
                <ProtectedRoute requiredPermission={{ module: 'users', action: 'view' }}>
                  <>
                    <Navbar />
                    <AdminAvailabilityPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/costs"
              element={
                <ProtectedRoute requiredPermission={{ module: 'reports', action: 'view' }}>
                  <>
                    <Navbar />
                    <CostReportPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/localization"
              element={
                <ProtectedRoute requiredPermission={{ module: 'permissions', action: 'edit' }}>
                  <>
                    <Navbar />
                    <LocalizationPage />
                  </>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AppProvider>
        </LocalizationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
